import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useMatch } from 'react-router';
import { Card } from '../../../../design-system';
import Alert from '../../../../helpers/alert';
import { successRefetch } from '../../../../helpers/data-loader';
import { findItemViaPathname } from '../../../../helpers/utils';
import AttachmentsBase from '../../../../components/Attachments';
import ModalForm from '../../../../components/ModalForm';
import SingleEntity from '../../../../components/SingleEntity';
import {
  Table,
  TableCardHeader,
  TableBody as TableBodyBase,
  CollapsibleRowTable as CollapsibleRowTableBase,
} from '../../../../components/Table';
import {
  COLUMNS,
  MODAL_FIELDS,
  SINGLE_ENTITY_FIELDS,
  CONTACT_COLUMNS,
  CONTACT_MODAL_FIELDS,
} from './constants';
import { ERR_MSG } from '../../../../globalConstants';

const Attachments = AttachmentsBase as React.ComponentType<any>;
const TableBody = TableBodyBase as React.ComponentType<any>;
const CollapsibleRowTable = CollapsibleRowTableBase as React.ComponentType<any>;

interface PartnershipsProps {
  data: { id: any; partnerships: any[]; all_files: any[]; updated_at?: any };
  pathname: string;
  navigate?: any;
  refetch: () => void;
  canEdit: boolean;
}

const Partnerships = ({
  data: { id, partnerships, all_files, updated_at: updatedAt },
  pathname,
  refetch,
  canEdit,
}: PartnershipsProps) => {
  const navigate = useNavigate();
  const [isOpenAddPartnership, setIsOpenAddPartnership] = useState(false);
  const [isOpenAddContact, setIsOpenAddContact] = useState(false);
  const [isOpenEditContact, setIsOpenEditContact] = useState(false);
  const [isOpenAddAttachment, setIsOpenAddAttachment] = useState(false);
  const [targetedContact, setTargetedContact] = useState<any>('');

  const selectedPartnership = findItemViaPathname(partnerships, pathname);
  const onPartnershipRoute = useMatch(`/vault/${id}/partnerships/:id`);

  useEffect(() => {
    if (onPartnershipRoute && !selectedPartnership) {
      navigate(-1);
    }
  }, [onPartnershipRoute, selectedPartnership, navigate]);

  const toggleAddPartnership = () => {
    setIsOpenAddPartnership(!isOpenAddPartnership);
  };

  const toggleAddContact = () => {
    setIsOpenAddContact(!isOpenAddContact);
  };

  const toggleEditContact = () => {
    setIsOpenEditContact(!isOpenEditContact);
  };

  const toggleAddAttachment = () => {
    setIsOpenAddAttachment(!isOpenAddAttachment);
  };

  const openEditModal = async (values) => {
    setTargetedContact(values);
    toggleEditContact();
  };

  const handlePartnershipCreate = async (values) => {
    try {
      await axios.post(`/client/${id}/partners`, values);
      refetch();
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };
  const handlePartnershipUpdate = async (values) => {
    try {
      await axios.put(
        `/client/${id}/partners/${selectedPartnership.id}`,
        values,
      );
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };
  const handleDeletePartnership = async (item) => {
    try {
      await axios.delete(`/client/${id}/partners/${item.id}`);
      successRefetch(refetch);
      navigate(`/vault/${id}/partnerships`);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleContactCreate = async (values) => {
    try {
      await axios.post(
        `/client/${id}/partnership/${selectedPartnership.id}/staff`,
        values,
      );
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleContactEdit = async (values) => {
    try {
      await axios.put(
        `/client/${id}/partnership/${selectedPartnership.id}/staff/${values.id}`,
        values,
      );
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleContactDelete = async (contact_id) => {
    try {
      await axios.delete(
        `/client/${id}/partnership/${selectedPartnership.id}/staff/${contact_id}`,
      );
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  return (
    <>
      <Card id="Partnerships-table">
        <TableCardHeader
          name="Partnerships"
          action="Add Partnership"
          onClick={toggleAddPartnership}
          disabled={!canEdit}
          updatedAt={updatedAt}
        />
        <TableBody maxHeight="22rem">
          <Table
            name="Partnerships"
            data={(partnerships ?? []).map((item) => ({
              ...item,
              handleDeletePartnership,
              canEdit,
            }))}
            columns={COLUMNS}
          />
        </TableBody>
        <ModalForm
          title="Add Partnership"
          isOpen={isOpenAddPartnership}
          onSubmit={handlePartnershipCreate}
          onClose={toggleAddPartnership}
          fields={MODAL_FIELDS}
          oneColumn
        />
      </Card>
      {onPartnershipRoute &&
        (!selectedPartnership ? null : (
          <>
            <SingleEntity
              data={selectedPartnership}
              fields={SINGLE_ENTITY_FIELDS}
              onSubmit={handlePartnershipUpdate}
              onClose={() => navigate(`/vault/${id}/partnerships`)}
              canEdit={canEdit}
            />
            <Card id="Contacts-table" mt={10}>
              <TableCardHeader
                name="Contacts"
                action="Add Contact"
                onClick={toggleAddContact}
                disabled={!canEdit}
              />
              <TableBody maxHeight="15rem">
                <CollapsibleRowTable
                  data={(selectedPartnership?.contacts ?? []).map((item) => ({
                    ...item,
                    openEditModal,
                    handleContactDelete,
                    canEdit,
                  }))}
                  columns={CONTACT_COLUMNS}
                />
              </TableBody>
              <ModalForm
                title="Add Contact"
                isOpen={isOpenAddContact}
                onSubmit={handleContactCreate}
                onClose={toggleAddContact}
                fields={CONTACT_MODAL_FIELDS}
                oneColumn
              />
              <ModalForm
                title="Edit Contact"
                isOpen={isOpenEditContact}
                onSubmit={handleContactEdit}
                onClose={toggleEditContact}
                fields={CONTACT_MODAL_FIELDS}
                oneColumn
                initialValues={targetedContact}
              />
            </Card>
            <Attachments
              data={all_files.filter(
                ({ model_type, model_id }) =>
                  model_type === 'VaultPartnerships' &&
                  model_id === selectedPartnership.id,
              )}
              canEdit={canEdit}
              isModalOpen={isOpenAddAttachment}
              onClick={toggleAddAttachment}
              onClose={toggleAddAttachment}
              modelId={selectedPartnership.id}
              modelType="VaultPartnerships"
              clientId={id}
              refetch={refetch}
              maxHeight="15rem"
              disabled={!canEdit}
            />
          </>
        ))}
    </>
  );
};

export default Partnerships;
